import React, { useEffect } from 'react';
import { tierLabel, tierVariant } from './recipeBookConstants';
import {
	recipeBookPanelClassName,
	recipeBookHealthBadgeClassName,
	recipeBookNavButtonClassName,
	recipeBookTierBadgeBaseClassName,
	tierBadgeClassName
} from './recipeBookStyles';

export default function RecipeBookDetail({
	recipe,
	recipes,
	onClose,
	onNavigate
}) {
	const index = recipes.findIndex((r) => r.id === recipe?.id);
	const hasPrev = index > 0;
	const hasNext = index >= 0 && index < recipes.length - 1;

	useEffect(() => {
		const handleKey = (e) => {
			if (!recipe) return;
			if (e.key === 'ArrowLeft' && hasPrev) onNavigate(recipes[index - 1]);
			if (e.key === 'ArrowRight' && hasNext) onNavigate(recipes[index + 1]);
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [recipe, recipes, index, hasPrev, hasNext, onNavigate, onClose]);

	if (!recipe) return null;

	const variant = tierVariant(recipe.health_tier);

	return (
		<div
			className={`${recipeBookPanelClassName} p-5 md:p-6`}
			role="article"
			aria-label={recipe.title}
		>
			<div className="flex items-start justify-between gap-3 mb-4">
				<div>
					<span
						className={`${recipeBookTierBadgeBaseClassName} ${tierBadgeClassName(variant)}`}
					>
						{tierLabel(recipe.health_tier)}
					</span>
					<h2 className="mt-1.5 text-xl font-bold text-gray-900 dark:text-dark-text">
						{recipe.title}
					</h2>
					{recipe.save_count > 1 ? (
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							Community saves: {recipe.save_count}
						</p>
					) : null}
				</div>
				<button
					type="button"
					onClick={onClose}
					className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none"
					aria-label="Collapse recipe"
				>
					−
				</button>
			</div>

			{recipe.health_badge ? (
				<p className={`${recipeBookHealthBadgeClassName} mb-2`}>{recipe.health_badge}</p>
			) : null}
			{recipe.description ? (
				<p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
					{recipe.description}
				</p>
			) : null}
			{recipe.source_verified && recipe.source_url ? (
				<a
					href={recipe.source_url}
					target="_blank"
					rel="noopener noreferrer"
					className="inline-block text-sm text-blue-700 dark:text-blue-400 underline mb-3 break-all"
				>
					View verified source recipe
				</a>
			) : (
				<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
					Community recipe from Save My Food — steps below are AI-suggested; no external source
					was verified.
				</p>
			)}
			{recipe.key_ingredients?.length ? (
				<p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
					Key ingredients: {recipe.key_ingredients.join(', ')}
				</p>
			) : null}
			{recipe.full_recipe?.summary ? (
				<p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{recipe.full_recipe.summary}</p>
			) : null}
			{recipe.full_recipe?.ingredients?.length ? (
				<div className="mb-3">
					<p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Ingredients</p>
					<ul className="mt-1 list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
						{recipe.full_recipe.ingredients.map((item, i) => (
							<li key={`ing-${i}`}>{item}</li>
						))}
					</ul>
				</div>
			) : null}
			{recipe.full_recipe?.steps?.length ? (
				<div className="mb-4">
					<p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Steps</p>
					<ol className="mt-1 list-decimal pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
						{recipe.full_recipe.steps.map((step, i) => (
							<li key={`step-${i}`}>{step}</li>
						))}
					</ol>
				</div>
			) : null}

			<div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-dark-border">
				<button
					type="button"
					disabled={!hasPrev}
					onClick={() => hasPrev && onNavigate(recipes[index - 1])}
					className={recipeBookNavButtonClassName}
				>
					← Previous
				</button>
				<span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
					{index + 1} of {recipes.length}
				</span>
				<button
					type="button"
					disabled={!hasNext}
					onClick={() => hasNext && onNavigate(recipes[index + 1])}
					className={recipeBookNavButtonClassName}
				>
					Next →
				</button>
			</div>
		</div>
	);
}
